import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  isExecutionActive,
  CompletionStatus,
} from "../../redux/reducers/Execution";
import { handleExecutionCompleted } from "../../redux/thunks/executionThunks";
import { reducerType } from "../../redux/reducers";
import { AppDispatch } from "../../redux/store";
import { getProject } from "../api/projects/projects";

// Poll the backend after 3 minutes if we haven't received a WebSocket completion.
const STALENESS_THRESHOLD_MS = 3 * 60 * 1000;

// Don't poll more than once every 10 seconds (e.g., rapid tab switches).
const MIN_POLL_INTERVAL_MS = 10 * 1000;

/**
 * Recovers from stuck executions when the browser misses the WebSocket
 * `complete` message (e.g., tab was backgrounded, VPN dropped frames).
 *
 * Checks the backend via `getProject()` when the tab regains focus or when
 * the execution has been active for too long. If `latestVersion` has been
 * bumped, the backend already finished — we trigger the normal completion flow.
 */
export function useExecutionRecovery(projectId: string | undefined): void {
  const dispatch = useDispatch<AppDispatch>();
  const executionState = useSelector((state: reducerType) => state.Execution);
  const projectState = useSelector((state: reducerType) => state.Projects);

  const executionStartRef = useRef<number | null>(null);
  const isPollingRef = useRef(false);
  const lastPollTimeRef = useRef(0);
  const preExecutionVersionRef = useRef<number | null>(null);

  // Keep a stable ref to the latest values so effects don't re-run on every
  // status transition (SENDING → QUEUED → IN_PROGRESS). Without this, the
  // staleness timer would restart on each sub-state change and might never fire.
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  const executionStatusRef = useRef(executionState.status);
  executionStatusRef.current = executionState.status;

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  // Snapshot the project version when execution starts so we can detect changes.
  useEffect(() => {
    if (isExecutionActive(executionState.status)) {
      if (executionStartRef.current === null) {
        executionStartRef.current = Date.now();
        preExecutionVersionRef.current =
          projectId && projectState.projects[projectId]
            ? projectState.projects[projectId].latestVersion
            : 0;
      }
    } else {
      executionStartRef.current = null;
      preExecutionVersionRef.current = null;
    }
  }, [executionState.status, projectId, projectState.projects]);

  // Polls the backend and recovers if the execution already completed.
  // Stored in a ref so effects can call the latest version without re-running.
  const attemptRecoveryRef = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  );
  attemptRecoveryRef.current = async (): Promise<void> => {
    const currentProjectId = projectIdRef.current;
    if (!currentProjectId || !isExecutionActive(executionStatusRef.current))
      return;
    if (isPollingRef.current) return;

    const now = Date.now();
    if (now - lastPollTimeRef.current < MIN_POLL_INTERVAL_MS) return;

    isPollingRef.current = true;
    lastPollTimeRef.current = now;

    try {
      const response = await getProject(currentProjectId);
      if (!response.success || !response.data) return;

      // Re-check after await — WebSocket may have completed the execution in the meantime.
      if (!isExecutionActive(executionStatusRef.current)) return;

      const backendVersion = response.data.latestVersion;
      const preVersion = preExecutionVersionRef.current ?? 0;

      if (backendVersion > preVersion) {
        dispatchRef.current(
          handleExecutionCompleted({
            status: CompletionStatus.COMPLETED,
            version: backendVersion,
          }),
        );
      }
    } catch {
      // Best-effort — don't break the UI if the poll fails.
    } finally {
      isPollingRef.current = false;
    }
  };

  // Trigger 1: Check when the user switches back to this tab.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        attemptRecoveryRef.current();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Trigger 2: Check after STALENESS_THRESHOLD_MS from when the execution started.
  // Uses the elapsed time so status sub-transitions don't restart the timer.
  useEffect(() => {
    if (!isExecutionActive(executionState.status)) return;

    const elapsed = executionStartRef.current
      ? Date.now() - executionStartRef.current
      : 0;
    const remaining = Math.max(STALENESS_THRESHOLD_MS - elapsed, 0);

    const timer = setTimeout(() => {
      attemptRecoveryRef.current();
    }, remaining);

    return () => clearTimeout(timer);
  }, [executionState.status]);
}
